/**
 * What the hover peek says about one movement.
 *
 * The 07.09 mock draws a card beside the hovered library row: a still, the movement's category, and
 * a figure with its worked muscles lit. Three of those four are decisions rather than rendering, so
 * they live here and the component is a `map` over the answer.
 *
 * ── Engagement is the wire's two lists, not a percentage ─────────────────────────────────────
 * The mock invents a share per region — `{ chest: 1, shoulders: .45, triceps: .3 }` — from a table
 * of six hand-written entries. **The wire has no such number.** `GetExerciseResponse` carries
 * `primaryMuscleGroups` and `secondaryMuscleGroups` and nothing between them, so a peek printing
 * "45%" would be printing a figure the backend never computed and cannot be held to.
 *
 * What this app already says about the difference is `muscleVolume`'s weighting — a primary group
 * is worth 1, a secondary one 0.5 — and that is the same statement in the only form the data
 * supports: two levels, named. So the peek draws two intensities of ONE neutral and labels them
 * "główna" and "wspomagająca" (see `muscleEmphasis`). Nothing here is a measured share and nothing
 * is printed as one.
 *
 * ── Both figures are always returned, and that is a layout decision as much as an honest one ──
 * Half the taxonomy lives on the back — `Triceps`, `Back`, `Glutes`, `Hamstrings`, `Calves` — so a
 * peek that drew only the front would show a blank body for a pulling movement. Returning the pair
 * unconditionally also fixes the card's height: the peek is one element that re-fills as the
 * pointer moves down the list, so a figure that came and went would resize a box that is centred on
 * its own height, and every row change would move it.
 *
 * ── A group with no anatomy simply does not paint ────────────────────────────────────────────
 * `REGIONS` has no entry for `Other` or `FullBody`, deliberately: the first has no location and the
 * second names every shape or none. Such a group is still LISTED — it is what the movement trains,
 * and dropping the word would be a second lie on top of an unpaintable one — it just lights nothing.
 *
 * No React, no DOM, no query hook, no i18next.
 */

import { muscleGroupToApi, stillUrl, type ExerciseListItem, type MuscleGroup } from '~/entities/exercise-library';
import { REGIONS } from '~/shared/ui/bodyModel/regions';
import { muscleGroupColour } from '~/shared/ui/muscleColour';
import type { BodySlug } from '~/shared/ui/bodyModel/bodyArtwork';

/** One muscle group the movement names, and how hard it works it. */
export interface ExercisePeekMuscle {
  readonly group: MuscleGroup;
  /** `true` for a primary group, `false` for a secondary one. There is no third level on the wire. */
  readonly isPrimary: boolean;
  /**
   * The paint the shapes this group lights are filled with — `muscleEmphasis` of its role.
   *
   * It fed the peek's pill dot as well until 17 September 2026, when the owner moved the pills onto
   * the plan rows' colourless language; the dot now takes `currentColor` from the word beside it
   * and this value reaches the FIGURE only. See `RailPeek.module.css`.
   */
  readonly ink: string;
}

export interface ExercisePeekView {
  /** The photograph to draw, or `null` when the catalogue holds none of this movement. */
  readonly stillUrl: string | null;
  /**
   * The hue the card's PLACEHOLDER plate is drawn in — the first primary group's, and the only
   * categorical colour left on this card since 16.09.2026.
   *
   * It stays a hue because the plate is the row's own fallback tile at the card's size: a movement
   * the catalogue holds no photograph of draws the same tinted plate in the list and in the peek,
   * and the owner's 15.09 round kept the list's colour deliberately. What the owner asked to go
   * neutral is the thing that carries a ROLE — the figure and the muscle words — and a plate that
   * stands in for a photograph carries none.
   */
  readonly tint: string | null;
  /** Fill per shape, for `BodySilhouette`. A shape no listed group reaches is simply absent. */
  readonly fillBySlug: Readonly<Partial<Record<BodySlug, string>>>;
  /** Primary groups first, each in the order the wire listed them. */
  readonly muscles: readonly ExercisePeekMuscle[];
}

/**
 * ── ONE hue on the figure, and INTENSITY carries the role (owner, 16 September 2026) ──────────
 *
 * The map used to paint each group in its own categorical token, so a compound movement drew a red
 * chest, a green shoulder and an amber triceps on one 132px figure and the trainer had to read a
 * legend to learn that none of those hues meant anything but "a different muscle". Owner: "jeden
 * uniwersalny odcień, większa intensywność zależnie od obciążenia, primary/secondary". The figure
 * has one thing to say — how hard this movement works this muscle — and it now says it with one
 * variable.
 *
 * **The hue is `--accent-ink`, the MUTED ember** — #9d4e24 in Flame, #ff7a45 in Flame Dark. Owner,
 * the same day, correcting a first pass that read the instruction as "a neutral grey": "zamień ten
 * szary na pomarańczowy taki jakiś naturalny nie oczojebny (w kolorze podświetlenia partii)". A lit
 * muscle is a highlight, and a highlight in this app's language is warm.
 *
 * **`--accent-ink` rather than `--accent`, and the numbers agree with the taste.** `--accent` is the
 * raw ember #ff5a1f in both Flame themes — the scalpel that marks a real action about four times a
 * screen, and this card can light eight shapes at once. It is also the LOUDER and the less legible
 * of the two here: measured on `--bg-chip`, the ground a lit shape is read against, the raw accent
 * reaches only 2.76:1 in Flame against `--accent-ink`'s 5.24:1, because it is a bright orange on a
 * bright ground. `--accent-ink` is the token this app already calls "the accent as WORDS", declared
 * per theme so a surface never has to know which theme it is in to pick the readable one.
 *
 * Measured on the composited pixels against `--bg-chip`:
 *
 * |                | Flame (light) | Flame Dark |
 * |---|---|---|
 * | primary, 100%  | 5.24:1 | 6.43:1 |
 * | secondary, 50% | 2.11:1 | 2.57:1 |
 *
 * **The secondary step is under the 3:1 a meaningful graphic owes, and it ships.** Raising the mix
 * until it clears puts the secondary within a hair's breadth of the primary and deletes the only
 * thing the figure has to say — and the same was true of every candidate tried, neutral or warm,
 * because the gap between the two levels IS the contrast being spent. The contrast is
 * carried the way this component already carries it for every fill it draws: every shape is RINGED
 * in `--border-3` (3.36:1 light, 3.63:1 dark), which is the decision recorded on
 * `BodySilhouette.module.css` and which accepted the old amber at 2.44:1 for the same reason. The
 * role is also written out in words on the pill beside the figure, so nothing here is colour-only
 * and no distinction is lost to a reader who cannot see the step.
 *
 * ── The two steps are `muscleVolume`'s weighting, unchanged ──────────────────────────────────
 * A primary group is worth 1 and a secondary one 0.5, and that is still the only difference the
 * wire supports. Expressed as a `color-mix` against `transparent` rather than as `opacity`, because
 * the value has to be a FILL: the figure's shapes overlap, and an element-level opacity would also
 * fade the hairline ring that makes each shape visible.
 *
 * This is the one place the step is written, and since 17 September 2026 the figure is the only
 * thing that reads it: the pills beside it went colourless with the rest of the plan rows'
 * language, and their dot follows their own word rather than this fill.
 */
const MUSCLE_INK = 'var(--accent-ink)';
const PRIMARY_MIX = '100%';
const SECONDARY_MIX = '50%';

function muscleEmphasis(isPrimary: boolean): string {
  const mix = isPrimary ? PRIMARY_MIX : SECONDARY_MIX;
  return `color-mix(in srgb, ${MUSCLE_INK} ${mix}, transparent)`;
}

/**
 * The peek's whole answer for one movement.
 *
 * Primary groups are applied AFTER secondary ones, so a group named on both lists — which the wire
 * does not forbid — lights at its primary strength rather than at whichever the iteration reached
 * last.
 */
export function exercisePeek(item: ExerciseListItem): ExercisePeekView {
  const muscles: ExercisePeekMuscle[] = [
    ...item.primaryMuscles.map((group) => ({
      group,
      isPrimary: true,
      ink: muscleEmphasis(true),
    })),
    ...item.secondaryMuscles
      .filter((group) => !item.primaryMuscles.includes(group))
      .map((group) => ({ group, isPrimary: false, ink: muscleEmphasis(false) })),
  ];

  // The group the card is READ BY — the first primary, or the first secondary on a movement the
  // catalogue named no primary for. It is the one place a categorical hue survives on this card
  // (see `tint`), which is why it is resolved from the group rather than from a muscle's ink.
  const leadGroup = muscles[0]?.group;

  const fillBySlug: Partial<Record<BodySlug, string>> = {};
  for (const entry of [...muscles].sort((left, right) => Number(left.isPrimary) - Number(right.isPrimary))) {
    const wireName = muscleGroupToApi(entry.group);
    // `Object.hasOwn` rather than a bare index: a key that reaches `Object.prototype` — `constructor`
    // is the live example — returns a function, which is not nullish, so `?? []` would never fire.
    const regions = Object.hasOwn(REGIONS, wireName) ? (REGIONS[wireName] ?? []) : [];
    for (const region of regions) {
      fillBySlug[region.muscle] = entry.ink;
    }
  }

  return {
    stillUrl: stillUrl(item),
    tint: leadGroup === undefined ? null : muscleGroupColour(leadGroup),
    fillBySlug,
    muscles,
  };
}
